import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation';
import { preloadEditButton } from '@confluence/edit-button/entry-points/preloadEditButton';
import { preloadFabricEditorEligibility } from '@confluence/edit-button/entry-points/preloadFabricEditorEligibility';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime';
import { preloadContentAnalyticsViewers } from '@confluence/confluence-analytics/entry-points/preloadContentAnalyticsViewers';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery';
import { preloadGrantAccessDialogData } from '@confluence/grant-access-dialog/entry-points/preloadGrantAccessDialogData';
import { preloadRendererContentState } from '@confluence/content-state/entry-points/preloadRendererContentState';
import { preloadFavouriteButton } from '@confluence/favourite-button/entry-points/preloadFavouriteButton';
import { preloadWatchDialog } from '@confluence/watch-dialog/entry-points/preloadWatchDialog';
import { preloadCommentButton } from '@confluence/comment-button/entry-points/preloadCommentButton';
import { preloadClassificationContextForViewContent } from '@confluence/data-classification/entry-points/queries';
import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { fg } from '@confluence/feature-gating';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';

import { ContentHeaderUnifiedQuery } from './ContentHeaderUnifiedQuery.graphql';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT, CONFLUENCE_EDITION } from './defaultQueryVariables';

export type preloadContentHeaderProps = {
	spaceKey?: string;
	contentId: string;
	isLicensed: boolean;
	userId: string | null;
	username?: string;
	edition: ConfluenceEdition | null;
	isPrevEditRoute?: boolean;
	useNewContentTopper?: boolean;
	isLivePageRoute?: boolean;
};

export function preloadContentHeader({
	spaceKey,
	contentId,
	isLicensed,
	userId,
	username,
	edition,
	isPrevEditRoute = false,
	useNewContentTopper = false,
	isLivePageRoute = false,
}: preloadContentHeaderProps) {
	const queries: Promise<any>[] = [];

	if (typeof window !== 'undefined') {
		if (!fg('confluence_preload_favourite_button') || !isLivePageRoute) {
			queries.push(preloadFavouriteButton(contentId, userId));
		}
	}

	// We preload content header unconditionally but when page is restricted we don't
	// render the corresponding components, neither we use the result of preloading.
	// Hence mute the restriction errors on preloading to avoid unnecessary noise.
	// The mute logic is done in muteExpectedGraphQLErrors, here we just want to know if errors happened.
	let hasErrors = false;
	const checkHasGraphQLErrors = (result: Awaited<ReturnType<typeof query>>) => {
		hasErrors ||= Boolean(result?.hasErrors);
		return result;
	};

	// Don't preload queries that will be updated by editor if previous route is edit_page
	if (!isPrevEditRoute) {
		queries.push(
			query({
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: ContentHeaderUnifiedQuery,
				variables: {
					contentId,
					spaceKey,
					versionOverride: null,
					embeddedContentRender: null,
					limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
					useNewContentTopper,
					includeGeneratedBy: fg('cc-activation-user-generated-by-byline'),
				},
			}).then(checkHasGraphQLErrors),
		);

		// Editor will preload the content state if it's a live page
		if (!isLivePageRoute) {
			queries.push(preloadRendererContentState(contentId).then(checkHasGraphQLErrors));
		}

		// SSR Grant Access Restrictions button
		// username is a legacy URL parameter that actually represents accountId in the grant access flow
		if (contentId && username) {
			queries.push(preloadGrantAccessDialogData(contentId, username));
		}

		//unlike SPA, no need to check the route since preloadContentHeader is fired only when its VIEW_PAGE.
		const includeAncestors = edition === CONFLUENCE_EDITION.FREE;
		queries.push(preloadPageRestrictionsQuery(contentId, includeAncestors));
	}

	const contentHeaderTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_preload_content_header_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		if (fg('confluence_frontend_usd_ssr')) {
			queries.push(preloadShareAndRestrictButtonQuery(contentId));
		}

		queries.push(
			preloadEditButton({ spaceKey, contentId }),
			slowQueryGuard(
				'preloadContentAnalyticsViewers',
				() => preloadContentAnalyticsViewers(contentId).then(checkHasGraphQLErrors),
				contentHeaderTimeout,
			),

			preloadReadTime({
				contentId,
				// preloadContentHeader is called by preloadViewPageRoute and preloadLivePageRoute only at the time of this writing. preloadViewPageRoute fetches the content body already.
				includeContentBody: isLivePageRoute,
				useCache: false,
			}),
			preloadCommentButton(contentId),
			/**
			 * Inline comment button:
			 * next/packages/ssr-app/src/module-replacements/@confluence/comment-button.js
			 *
			 * "..." button:
			 * See next/packages/ssr-app/src/module-replacements/@confluence/content-tools.js
			 *
			 * Share button:
			 * See next/packages/ssr-app/src/module-replacements/@confluence/share.js
			 */

			// Determine whether to show restriction and share button
			preloadWebItemLocation({
				location: 'system.content.button',
				contentId,
			}),
			preloadClassificationContextForViewContent(contentId),
		);

		queries.push(preloadFabricEditorEligibility(contentId));

		if (!isLivePageRoute && fg('confluence_frontend_content_wrapper')) {
			// preload data for whether "unpublished changes" label should show in view page object header
			queries.push(
				preloadWebItemLocation({
					location: 'page.metadata.banner',
					contentId,
				}),
			);
		}
	} else {
		queries.push(preloadWatchDialog({ contentId, isLicensed, userId }));
		queries.push(preloadEditButton({ spaceKey, contentId }));
		queries.push(
			slowQueryGuard(
				'preloadContentAnalyticsViewers',
				() => preloadContentAnalyticsViewers(contentId).then(checkHasGraphQLErrors),
				contentHeaderTimeout,
			),
		);

		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!isLivePageRoute &&
			fg('confluence_frontend_content_wrapper')
		) {
			// preload data for whether "unpublished changes" label should show in view page object header
			queries.push(
				preloadWebItemLocation({
					location: 'page.metadata.banner',
					contentId,
				}),
			);
		}
	}

	return waitForAllReturnWhenError(queries).then((errors) => Boolean(errors || hasErrors));
}
